import { useState, type CSSProperties } from "react";
import dayjs, { type Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import Box from "@mui/material/Box";

// antd <DatePicker> (dayjs values) -> MUI x-date-pickers on AdapterDateFns.
// The app is committed to ONE adapter (date-fns); importing AdapterDayjs would
// augment MUI's global date type and break existing Date-based pickers. So we
// keep the antd Dayjs contract at the boundary and convert Dayjs<->Date inside.

type PickerKind = "date" | "week" | "month" | "quarter" | "year";

const VIEWS: Record<PickerKind, Array<"year" | "month" | "day">> = {
  date: ["year", "month", "day"], week: ["year", "month", "day"],
  month: ["year", "month"], quarter: ["year", "month"], year: ["year"],
};
const DEFAULT_FMT: Record<PickerKind, string> = {
  date: "YYYY-MM-DD", week: "YYYY-[W]ww", month: "YYYY-MM", quarter: "YYYY-[Q]Q", year: "YYYY",
};
// Which view a picker opens on. MUI defaults a year+month picker to the YEAR grid,
// so a month picker opened on a wall of years — open it on the month grid instead.
const OPEN_TO: Record<PickerKind, "year" | "month" | "day"> = {
  date: "day", week: "day", month: "month", quarter: "month", year: "year",
};

// dayjs (or moment — both expose .toDate()) value <-> JS Date for the MUI picker.
const toDate = (d?: Dayjs | { toDate: () => Date; isValid: () => boolean } | null): Date | null => {
  if (!d) return null;
  const anyD = d as unknown as { toDate?: () => Date; isValid?: () => boolean };
  if (typeof anyD.toDate === "function") {
    return anyD.isValid && !anyD.isValid() ? null : anyD.toDate();
  }
  return new Date(d as unknown as string);
};
const toDayjs = (d: Date | null): Dayjs | null => (d ? dayjs(d) : null);
// dayjs format tokens -> date-fns tokens (Y->y, D->d) outside [literal] blocks.
const toDateFnsFmt = (f: string): string =>
  f.split(/(\[[^\]]*\])/).map((seg) => (seg.startsWith("[") ? seg.slice(1, -1) : seg.replace(/Y/g, "y").replace(/D/g, "d"))).join("");

// Accept dayjs OR moment (both expose toDate/isValid) for value, since some
// call sites still pass moment objects during the migration off moment.
export type DateLike = Dayjs | { toDate: () => Date; isValid: () => boolean } | null;

export interface DatePickerProps {
  value?: DateLike;
  defaultValue?: DateLike;
  // date param loose (dayjs at runtime; some call sites still type it Moment mid-migration).
  onChange?: (date: any, dateString: string) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  format?: string;
  picker?: PickerKind;
  /** View the calendar opens on. Defaults sensibly from `picker` (month->month, year->year, else day). */
  openTo?: "year" | "month" | "day";
  disabled?: boolean;
  allowClear?: boolean;
  // antd validation status; maps to MUI TextField error state.
  status?: "error" | "warning" | "" | false | undefined;
  placeholder?: string;
  size?: "small" | "middle" | "large";
  disabledDate?: (current: any) => boolean; // eslint-disable-line @typescript-eslint/no-explicit-any
  minDate?: Dayjs;
  maxDate?: Dayjs;
  style?: CSSProperties;
  className?: string;
  id?: string;
  "data-cy"?: string;
  "data-testid"?: string;
}

export const DatePicker = ({
  value, defaultValue, onChange, format, picker = "date", openTo, disabled, placeholder,
  size, status, disabledDate, minDate, maxDate, style, className, id,
  "data-cy": dataCy, "data-testid": dataTestid,
}: DatePickerProps) => {
  const dayjsFmt = format ?? DEFAULT_FMT[picker];
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MuiDatePicker
        // Render a single plain <input> (antd parity) instead of the v8 accessible
        // section list, so data-cy/id/placeholder land on one typeable input.
        enableAccessibleFieldDOMStructure={false}
        // Only pass `value` when controlled; passing value={null} for an
        // uncontrolled (defaultValue-only) picker would blank the field.
        {...(value !== undefined ? { value: toDate(value) } : {})}
        {...(defaultValue !== undefined ? { defaultValue: toDate(defaultValue) } : {})}
        disabled={disabled}
        format={toDateFnsFmt(dayjsFmt)}
        views={VIEWS[picker]}
        openTo={openTo ?? OPEN_TO[picker]}
        minDate={toDate(minDate) ?? undefined}
        maxDate={toDate(maxDate) ?? undefined}
        shouldDisableDate={disabledDate ? (d: Date) => disabledDate(dayjs(d)) : undefined}
        onChange={(d: Date | null) => {
          const dj = toDayjs(d);
          onChange?.(dj, dj ? dj.format(dayjsFmt) : "");
        }}
        // fullWidth: antd DatePicker fills its form container; MUI is content-width. An explicit style.width (inline) still wins.
        slotProps={{ textField: { size: size === "large" ? "medium" : "small", fullWidth: true, placeholder, style, className, error: status === "error", id, inputProps: { "data-cy": dataCy, "data-testid": dataTestid } } }}
      />
    </LocalizationProvider>
  );
};

export type DayjsRange = [DateLike | undefined, DateLike | undefined];

export interface RangePickerProps {
  value?: DayjsRange;
  defaultValue?: DayjsRange;
  // dates param loose for the same moment/dayjs mid-migration reason.
  onChange?: (dates: any, dateStrings: [string, string]) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  format?: string;
  picker?: PickerKind;
  disabled?: boolean;
  placeholder?: [string, string];
  size?: "small" | "middle" | "large";
  disabledDate?: (current: any) => boolean; // eslint-disable-line @typescript-eslint/no-explicit-any
  allowEmpty?: boolean[];
  allowClear?: boolean;
  style?: CSSProperties;
  className?: string;
}

const RangePicker = ({ value, defaultValue, onChange, format, picker = "date", disabled, placeholder = ["Start date", "End date"], size, style, className }: RangePickerProps) => {
  const [internal, setInternal] = useState<DayjsRange>(defaultValue ?? [null, null]);
  const range = value ?? internal;
  const dayjsFmt = format ?? DEFAULT_FMT[picker];
  const dfFmt = toDateFnsFmt(dayjsFmt);
  const fmtOf = (d: DayjsRange[number]): string => {
    const anyD = d as unknown as { format?: (f: string) => string };
    return anyD && typeof anyD.format === "function" ? anyD.format(dayjsFmt) : "";
  };
  const update = (next: DayjsRange) => {
    if (value === undefined) setInternal(next);
    onChange?.(next, [fmtOf(next[0]), fmtOf(next[1])]);
  };
  const commonSize = size === "large" ? ("medium" as const) : ("small" as const);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} style={style} className={className}>
        <MuiDatePicker
          enableAccessibleFieldDOMStructure={false}
          value={toDate(range[0])} disabled={disabled} format={dfFmt} views={VIEWS[picker]}
          maxDate={toDate(range[1]) ?? undefined}
          onChange={(d: Date | null) => update([toDayjs(d), range[1]])}
          slotProps={{ textField: { size: commonSize, fullWidth: true, placeholder: placeholder?.[0] } }}
        />
        <Box component="span" aria-hidden>–</Box>
        <MuiDatePicker
          enableAccessibleFieldDOMStructure={false}
          value={toDate(range[1])} disabled={disabled} format={dfFmt} views={VIEWS[picker]}
          minDate={toDate(range[0]) ?? undefined}
          onChange={(d: Date | null) => update([range[0], toDayjs(d)])}
          slotProps={{ textField: { size: commonSize, fullWidth: true, placeholder: placeholder?.[1] } }}
        />
      </Box>
    </LocalizationProvider>
  );
};

DatePicker.RangePicker = RangePicker;

export default DatePicker;
